import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ScrollService } from '../../scroll.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { MESSAGE_DELAY } from 'src/app/shared/constants/global.constants';
import { ChatBotFlowSaService } from '../ui-chat-bot-sa/chat-bot-flow-sa.service';
import { EMPTY, Subject, catchError } from 'rxjs';
import {
  FlowDesign,
  EmployeeSelectionStepData,
  CustomVariable,
  WordOfMouthStepDataApiResponse
} from '../../types/steps';
import {
  Step,
  StepMessage,
  StepType,
  StepAction
} from '../../types/steps.type';
import { CustomVariablesService } from '../ui-chat-bot-sa/custom-variables.service';
import { TranslateService } from '@ngx-translate/core';
import { WebShareApiService } from 'src/app/shared/services/web-share-api.service';
import { StepApiDataPreparationService } from 'src/app/shared/services/step-api-data-preaparation.service';
import { UserMessageFieldComponent } from '../user-message-field/user-message-field.component';
import { FocusManagementService } from 'src/app/shared/services/focus-management.service';
import { AiFeedbackStep } from '../../types/steps/ai-feedback-step.interface';
import { AgentMessageFieldComponent } from '../agent-message-field/agent-message-field.component';

@Component({
  selector: 'chat-step',
  standalone: false,

  templateUrl: './chat-step.component.html',
  styleUrls: ['./chat-step.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ])
    ]),
    trigger('scale', [
      transition(':enter', [
        style({
          opacity: 0
        }),
        animate(
          `${MESSAGE_DELAY - 200}ms ease-in`,
          style({
            opacity: 1
          })
        )
      ])
    ])
  ]
})
export class ChatStepComponent implements OnInit {
  womChannels: WordOfMouthStepDataApiResponse | null = null;
  private destroy$ = new Subject<void>();
  @ViewChild('agentWrapper') agentIcon!: ElementRef;
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild(UserMessageFieldComponent)
  userMessageField?: UserMessageFieldComponent;
  @ViewChildren('activeQuestionMessage')
  activeQuestionMessages?: QueryList<AgentMessageFieldComponent>;
  offsetY: number = 0;
  previousHeight: number = 0;
  @Output()
  scrollToBottom: EventEmitter<void> = new EventEmitter<void>();
  @Input() step: Step | null = null;
  @Input() flowDesign: FlowDesign | null = null;
  @Input() currentLang!: string;
  @Input() testMode?: boolean;
  @Input() isLastStep = false;
  // Set when this step was already answered before a forced reload. Renders the
  // step as answered instead of replaying its message animation and re-arming
  // its options.
  @Input() restoredAnswer: { [lang: string]: string } | null = null;
  fadeInState: string = 'hidden';
  userMessage: { [lang: string]: string } | undefined;
  displayedMessages: StepMessage[] = [];
  allAgentMessagesDisplayed: boolean = false;
  inputValues: Record<string, string> = {};
  feedback: string = '';
  stepType = StepType;
  dynamicVariables: CustomVariable | null = null;
  stepCssClasses: string | undefined;
  agentIconOpacity: number = 1; // Default opacity for the agent icon
  errorMessage: string | undefined;
  feedbackValidationAttempt: number = 0;
  private selectedEmployee: EmployeeSelectionStepData | null = null;
  isAiFlow: boolean = false;
  isLoading: boolean = false;
  hasAiTextAnswer = false;
  countdownMessage: string = '';
  constructor(
    private chatBotFlowSaService: ChatBotFlowSaService,
    private scrollService: ScrollService,
    private cdr: ChangeDetectorRef,
    private customVariablesService: CustomVariablesService,
    private translateService: TranslateService,
    private webShareService: WebShareApiService,
    private stepApiDataPreparationService: StepApiDataPreparationService,
    private focusManagementService: FocusManagementService
  ) {}

  ngOnInit(): void {
    if (this.step && this.restoredAnswer) {
      // Show the whole step at once - no typing animation - and surface the
      // answer the user already gave. `showMessagesWithDelay` is skipped because
      // `allAgentMessagesDisplayed` is already true.
      this.displayedMessages = [...this.step.messages];
      this.allAgentMessagesDisplayed = true;
      const hasAnswerText = Object.values(this.restoredAnswer).some((text) =>
        text?.trim()
      );
      // Filler steps that auto-advanced have no visible answer to show.
      this.userMessage = hasAnswerText ? this.restoredAnswer : undefined;
    }
    if (this.step) {
      // fix this later to set it only once to avoid unnecessary checks on every step
      if (this.step.type === StepType.AI_FEEDBACK) {
        this.isAiFlow = true;
      }
      this.stepCssClasses = `${this.step.type.toLowerCase()}-step-wrapper step-${this.step.step_id}`;
      if (this.step.type === StepType.ONLINE_REVIEW) {
        const googleReviewVar =
          this.customVariablesService.getVariable('google_review_link');

        if (googleReviewVar && googleReviewVar.trim() !== '') {
          // Find the specific action for Google Review based on platform property
          const googleAction = this.step.actions.find(
            (a) => a.action?.platform === 'google_review'
          );

          if (googleAction) {
            (googleAction.action as any).redirect = googleReviewVar;
          }
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (
      this.messagesContainer &&
      this.messagesContainer.nativeElement &&
      !this.allAgentMessagesDisplayed
    ) {
      this.previousHeight = this.messagesContainer.nativeElement.offsetHeight;
      this.showMessagesWithDelay();
    }
  }

  showMessagesWithDelay() {
    let index = 0;
    const addNextMessage = () => {
      if (this.step && index < this.step.messages.length) {
        const message = this.step.messages[index];
        if (message && message.message) {
          this.displayedMessages.push(message); // if you want to access the first object
        }
        index++;
        // this trigger transition immediately even if message hight is larger then 2 lines
        setTimeout(() => {
          this.updateIconPosition();
        });
        setTimeout(() => {
          this.scrollService.scrollToBottom();
        }, 100);
        setTimeout(() => {
          // After the last message
          if (this.step && index >= this.step.messages.length) {
            setTimeout(() => {
              this.allAgentMessagesDisplayed = true;
              this.cdr.detectChanges();
              this.focusStepQuestionIfNeeded();
            }, MESSAGE_DELAY);
          } else {
            // If there are still messages left, set a new timeout for the next one
            setTimeout(addNextMessage, MESSAGE_DELAY);
          }
          this.cdr.detectChanges();
        }, MESSAGE_DELAY);
      }
    };

    addNextMessage();
  }

  private async handleWordOfMouthChannels(option: StepAction): Promise<void> {
    this.womChannels = this.chatBotFlowSaService.getWomChannels();
    if (this.womChannels) {
      const redirectUrl = this.webShareService.getChannelUrlFromService(
        option,
        this.currentLang
      );
      if (redirectUrl) {
        this.webShareService.handleRedirect({
          url: redirectUrl,
          channelType: option.icon
        });
        return;
      }
    }
  }

  setNextStep(option: StepAction, errorMessage?: string) {
    let isSkipButton = option?.button?.['style'] === 'outline';
    if (errorMessage) {
      this.errorMessage = errorMessage;
      if (this.step?.type === StepType.FEEDBACK) {
        this.feedbackValidationAttempt++;
      }
      return;
    }
    // Triggers API call if step has API config
    if (this.step) {
      if (isSkipButton) {
        // A skipped FEEDBACK step still takes its answer slots, so later answers keep theirs
        const feedbackSlots =
          this.step.type === StepType.FEEDBACK
            ? (this.step.api?.data?.feedbacks?.length ?? 0)
            : 0;
        this.chatBotFlowSaService.notifyStepSkipped(feedbackSlots).subscribe();
      }

      if (this.step.type === StepType.MESSAGE) {
        setTimeout(() => {
          this.agentIconOpacity = 0;
        }, MESSAGE_DELAY);
      }

      if (this.step?.type === StepType.WORD_OF_MOUTH_CHANNELS) {
        this.handleWordOfMouthChannels(option);
      }
      if (this.step?.type === StepType.AI_FEEDBACK && !isSkipButton) {
        this.isLoading = true;
      }

      if (this.step.api && !this.testMode && !isSkipButton) {
        this.stepApiDataPreparationService.prepareApiData(
          this.step,
          option,
          this.currentLang
        );
        this.submitStepApi();
      }
      // Handle redirects
      this.handleRedirect(option);
      // Opens a new tab if redirect is present

      const delay =
        this.step.type === StepType.MESSAGE
          ? MESSAGE_DELAY
          : MESSAGE_DELAY + 400;

      setTimeout(() => {
        if (!this.isAiFlow || isSkipButton) {
          this.chatBotFlowSaService.addStepToSortedStepsByStepNumber(
            option.action.jump
          );
        }
      }, delay);
      // Set user message
      this.setUserMessage(option);
      // Persist the answer straight away, so a reload triggered before the
      // delayed step advance still restores this step as answered.
      this.chatBotFlowSaService.recordAnswerForCurrentStep(this.userMessage);
      this.focusSubmittedUserMessage();
    }
  }

  onCountdownMessage(message: string): void {
    this.countdownMessage = message;
    this.agentIconOpacity = 0;
  }

  trackByFn(index: number, item: any): any {
    return item?.id || index;
  }
  updateIconPosition() {
    setTimeout(() => {
      const currentHeight = this.messagesContainer.nativeElement.offsetHeight;
      const heightDifference = currentHeight - this.previousHeight;
      this.offsetY += heightDifference;
      this.previousHeight = currentHeight;
    });
  }
  onContactFieldValueChange(event: { key: string; value: string }): void {
    if (this.step && this.step.type === StepType.CONTACT_FORM) {
      this.inputValues[event.key] = event.value;
      this.step.api.data = { ...this.step.api.data, ...this.inputValues };
    }
  }

  // We run this method if user does not select skip option
  submitEmployeeSelection(employee: EmployeeSelectionStepData) {
    this.selectedEmployee = employee;
    if (this.step?.type === StepType.EMPLOYEE_SELECTION) {
      this.step.api.data.condition_value = employee.subdivision_id;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private submitStepApi(): void {
    if (!this.step?.api) return;

    this.chatBotFlowSaService
      .updateFeedbackApi(this.step.api)
      .pipe(
        catchError((error) => {
          // Recording the selection is best-effort: handleRedirect already
          // advanced the user, so a rejected submission (e.g. HTTP 422) must
          // not surface as an unhandled Angular error.
          this.isLoading = false;
          console.error('Feedback API submission failed', error);
          return EMPTY;
        })
      )
      .subscribe((response) => {
        if (!response) return;
        if (this.step!.type === StepType.AI_FEEDBACK) {
          if (!response.step) {
            response.step = Math.floor(Math.random() * 1_000_000_000);
          }
          if (!response.step_id) {
            response.step_id = response.step;
          }
          if (!response.actions) {
            console.warn(
              'AI_FEEDBACK response is missing actions; proceeding with empty actions array.',
              response
            );
            response.actions = [];
          }
          // if (!response.api)
          // response.api = this.aiApiConfig;
          // }

          console.log('Adding next AI step to the flow:', response);
          this.chatBotFlowSaService.addCustomStep(response as Step);
          this.isLoading = false;
          this.chatBotFlowSaService.addStepToSortedStepsByStepNumber(
            response.step
          );
        }
        // Handle WORD_OF_MOUTH response
        if (this.step?.type === StepType.WORD_OF_MOUTH) {
          this.chatBotFlowSaService.setWomChannels(
            response as WordOfMouthStepDataApiResponse
          );
        }

        // Legacy funnel redirect logic (to be removed after migration)
        if (
          this.step?.type === StepType.WORD_OF_MOUTH_CHANNELS &&
          response.recommendation_message &&
          !this.womChannels
        ) {
          this.handleLegacyWomRedirect(response.recommendation_message);
        }

        // Handle CONTACT_FORM_EXTERNAL redirect
        if (response.redirect_url) {
          setTimeout(() => {
            window.open(response.redirect_url, '_blank');
          }, 100);
        }

        // Handle EMPLOYEE_SELECTION variables
        if (
          response.variables &&
          this.step?.type === StepType.EMPLOYEE_SELECTION
        ) {
          this.customVariablesService.updateVariables(response.variables);
        }
      });
  }
  private handleLegacyWomRedirect(
    message: string | { mobile: string; desktop: string }
  ): void {
    let redirectUrl: string | null = null;

    if (typeof message === 'string') {
      redirectUrl = message;
    } else {
      const isMobile =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          navigator.userAgent
        ) || window.innerWidth <= 768;
      redirectUrl = isMobile ? message.mobile : message.desktop;
    }

    if (redirectUrl) {
      setTimeout(() => {
        window.open(redirectUrl, '_blank');
      }, 100);
    }
  }
  private handleRedirect(option: StepAction): void {
    if (!this.step || !option.action.redirect) return;

    // Skip redirect for WORD_OF_MOUTH_CHANNELS (handled separately)
    if (this.step.type === StepType.WORD_OF_MOUTH_CHANNELS) return;

    let optionLink: string;

    if (this.step.type === StepType.ONLINE_REVIEW) {
      optionLink = option.action.redirect as string;
    } else if (
      typeof option.action.redirect === 'object' &&
      option.action.redirect !== null
    ) {
      optionLink = option.action.redirect[this.currentLang];
    } else {
      optionLink = option.action.redirect;
    }

    window.open(optionLink, '_blank');
  }
  private toAllLangs(text: string): { [lang: string]: string } {
    return { en: text, de: text, fr: text, it: text };
  }

  private setUserMessage(option: StepAction): void {
    if (!this.step) return;

    if (this.step.type === StepType.FEEDBACK) {
      this.userMessage = this.toAllLangs(this.formatFeedbackMessage(option));
    } else if (this.step.type === StepType.AI_FEEDBACK) {
      this.userMessage = this.toAllLangs(
        this.step.api.data.user_prompt || option.text[this.currentLang]
      );
    } else if (this.step.type === StepType.CONTACT_FORM) {
      this.userMessage = this.toAllLangs(this.formatContactFormMessage());
    } else if (this.step.type === StepType.EMPLOYEE_SELECTION) {
      this.userMessage = this.toAllLangs(
        this.selectedEmployee?.employee_name || 'Person selected'
      );
    } else {
      this.userMessage = option.text;
    }
  }

  // A blank free-text answer would send an empty prompt to the AI, so only
  // skipping is allowed until the user writes something visible
  get isAiTextAnswerMissing(): boolean {
    return (
      this.step?.type === StepType.AI_FEEDBACK &&
      (this.step as AiFeedbackStep).feedback_type !== 'checkbox' &&
      !this.hasAiTextAnswer
    );
  }

  get activeQuestionMessageId(): string | null {
    return this.step ? `chat-step-question-${this.step.step_id}` : null;
  }

  get shouldAutoFocusFirstControl(): boolean {
    if (!this.step) {
      return false;
    }

    if (
      this.step.type === StepType.CONTACT_FORM ||
      this.step.type === StepType.EMPLOYEE_SELECTION
    ) {
      return true;
    }

    if (this.step.type === StepType.AI_FEEDBACK) {
      const aiFeedbackStep = this.step as AiFeedbackStep;
      return aiFeedbackStep.feedback_type !== 'checkbox';
    }

    if (this.step.type !== StepType.FEEDBACK) {
      return false;
    }

    return (
      this.step.additional_config?.feedback_blocks?.some(
        (feedback) =>
          feedback.feedback_type === 'textarea' ||
          feedback.feedback_type === 'textfield'
      ) ?? false
    );
  }

  get shouldFocusQuestionFirst(): boolean {
    return (
      !!this.step &&
      !this.shouldAutoFocusFirstControl &&
      this.step.actions.some(
        (action) => !!action.text?.[this.currentLang]?.trim()
      )
    );
  }

  private focusSubmittedUserMessage(): void {
    setTimeout(() => {
      this.userMessageField?.focus();
    });
  }

  private focusStepQuestionIfNeeded(): void {
    if (!this.shouldFocusQuestionFirst) {
      return;
    }

    setTimeout(() => {
      if (this.focusManagementService.canApplyProgrammaticFocus()) {
        this.activeQuestionMessages?.last?.focus();
      }
    });
  }

  private formatFeedbackMessage(option: StepAction): string {
    const apiData = this.step?.api?.data as any;
    const feedbacks = apiData?.feedbacks || [];
    const items = feedbacks
      .map((data: any) => {
        if (data.categories?.length) {
          return { value: data.categories.join(', '), isCheckbox: true };
        } else if (data.text?.trim()) {
          return { value: data.text.trim(), isCheckbox: false };
        }
        return null;
      })
      .filter(Boolean);

    if (items.length === 0) return option.text[this.currentLang];
    if (items.length === 1) return items[0].value;

    return items
      .map((item: any, index: number) => {
        const label = item.isCheckbox
          ? this.translateService.instant('selection')
          : this.translateService.instant('answer');

        const sameTypeItems = items.filter(
          (i: any) => i.isCheckbox === item.isCheckbox
        );
        const number =
          sameTypeItems.length > 1
            ? ` ${items.filter((i: any, idx: number) => idx <= index && i.isCheckbox === item.isCheckbox).length}`
            : '';

        return `<b>${label}${number}:</b> ${item.value}`;
      })
      .join('<br>');
  }

  private formatContactFormMessage(): string {
    let formattedMessage = '';

    if (
      this.step?.type === StepType.CONTACT_FORM &&
      'additional_config' in this.step &&
      this.step.additional_config?.contact_fields
    ) {
      this.step.additional_config.contact_fields.forEach((field) => {
        if (field.value) {
          const fieldLabel = field.text[this.currentLang] || field.type;
          formattedMessage += `<b>${fieldLabel}:</b> ${field.value}<br>`;
        }
      });
    }

    return formattedMessage;
  }
}
